// The starter library every company sees under Templates: professional
// starting points. They're read-only; "Save as" copies one into the company's
// own templates to edit. Bodies use the same small markup everywhere:
//   "## Heading"   a section heading
//   "- item"       a bullet
//   [Something]    a blank the company fills in after Save as
//   {{field}}      filled from the customer / job / company when used
// Terms (estimate, invoice) stay plain: they print on documents as-is.
// These are business templates, not legal advice; the library says so.

import { LIEN_DOC_TITLE, LIEN_WAIVER_KINDS, LIEN_WAIVER_WHEN, lienNoticeBody, lienWaiverBody } from "./liens";

export interface StarterTemplate {
  key: string;
  name: string;
  category: string;
  /** One line on what it's for. */
  description: string;
  requiresSignature: boolean;
  /** Who signs (default: the customer). */
  signer?: "customer" | "company";
  body: string;
}

const t = (s: string) => s.trim().replace(/^ +/gm, "");

export const STARTER_TEMPLATES: StarterTemplate[] = [
  // ---------- Estimate terms ----------
  {
    key: "estimate-residential",
    name: "Estimate terms: Residential",
    category: "Estimate Terms",
    description: "For homeowners: price hold, deposit, scheduling, what's not included, warranty, cancellation.",
    requiresSignature: false,
    body: t(`
      1. Price. This estimate is good for [30] days. Prices include the parts and labor listed. Anything not listed is not included.
      2. Deposit. A deposit of [50%] is due when you approve this estimate. It orders your materials and holds your place on our schedule. The balance is due when the work is complete.
      3. Scheduling. We'll contact you to schedule once materials arrive. Special-order items can take [2-4] weeks.
      4. Changes. If you ask for changes, or we find conditions that couldn't be seen when we quoted (hidden damage, code issues, structural problems), we'll explain the cost and get your OK before doing extra work.
      5. Not included unless listed: permits and permit fees, electrical work beyond connecting to an existing outlet, drywall, paint, haul-away of debris other than our own, and repairs to framing or structure.
      6. Site. Please make sure the work area is clear and that we have access and power on the scheduled day. A return trip caused by no access may be charged at our trip rate.
      7. Warranty. Our workmanship is warranted for [1 year] from completion. Manufacturer warranties on parts and products apply as stated by the manufacturer.
      8. Cancellation. You may cancel within 3 business days of approving this estimate for a full refund of your deposit. After that, special-order materials already purchased are not refundable.
      9. Payment. We accept [cash, check, and major credit cards]. Unpaid balances after [30] days may be charged [1.5%] per month.
    `),
  },
  {
    key: "estimate-commercial",
    name: "Estimate terms: Commercial and builder",
    category: "Estimate Terms",
    description: "For businesses and builders: POs, progress billing, Net terms, site readiness, change orders.",
    requiresSignature: false,
    body: t(`
      1. Validity. Pricing is valid for [30] days from the estimate date and is based on the scope, quantities and drawings provided.
      2. Purchase order. A purchase order or signed acceptance is required before materials are ordered. Please reference the PO number on all correspondence.
      3. Billing. Work is billed [on completion / progress-billed by phase or lot]. Invoices are due Net [30] days from the invoice date.
      4. Site readiness. Pricing assumes the opening is framed, finished and ready, with power at the location, clear access, and no other trades working in the area. A return trip caused by the site not being ready may be billed at [$X] per trip.
      5. Change orders. Work outside this scope, including design changes, added openings and corrections to work by others, requires a written change order before we proceed.
      6. Exclusions unless listed: permits, engineering, electrical rough-in, framing corrections, finish carpentry, paint and patching.
      7. Schedule. Lead times start when we receive the PO and any required selections. We'll confirm install dates in writing.
      8. Warranty. Workmanship is warranted for [1 year] from the completion date. Manufacturer warranties apply to products. Warranty does not cover damage from misuse, impact, or work by others.
      9. Lien rights. We reserve all lien rights available under state law until paid in full. Lien waivers are provided on request against payment received.
    `),
  },

  // ---------- Invoice terms ----------
  {
    key: "invoice-due-on-receipt",
    name: "Invoice terms: Due on receipt",
    category: "Invoice Terms",
    description: "Homeowner service: due on receipt, late fee, returned checks, warranty reminder.",
    requiresSignature: false,
    body: t(`
      Payment is due on receipt. Thank you for your business.
      Balances unpaid after [30] days may be charged [1.5%] per month. A [$35] fee applies to returned checks.
      Your workmanship warranty and any manufacturer warranties start on the completion date shown above. Keep this invoice for your records.
    `),
  },
  {
    key: "invoice-net-30",
    name: "Invoice terms: Net 30",
    category: "Invoice Terms",
    description: "Commercial and builder accounts: Net 30, PO reference, remittance, lien rights.",
    requiresSignature: false,
    body: t(`
      Payment is due within 30 days of the invoice date. Please include the invoice number and your PO number with payment.
      Past-due balances may be charged [1.5%] per month, and work on open orders may be paused until the account is current.
      We reserve all lien rights available under state law until this invoice is paid in full. Lien waivers are provided on request against payment received.
      Questions about this invoice? Contact our office within 10 days of the invoice date.
    `),
  },

  // ---------- Service agreements ----------
  {
    key: "plan-monthly",
    name: "Maintenance plan: Monthly",
    category: "Service Agreements",
    description: "A month-to-month plan with a visit every month, billed monthly, cancel anytime.",
    requiresSignature: true,
    body: t(`
      This Maintenance Plan is between {{company.name}} ("we", "us") and {{customer.name}} ("you") for service at {{customer.address}}, starting {{date}}.

      ## What's included
      - One scheduled maintenance visit each month.
      - At each visit: inspect, adjust, lubricate and test the equipment covered by this plan, and tell you about anything that needs attention.
      - Priority scheduling for repair calls ahead of non-members.
      - [10%] off parts and labor for repairs while the plan is active.

      ## What's not included
      Parts, repairs and replacements are not included in the plan price unless listed. We'll always give you the price and get your OK before doing repair work.

      ## Price and billing
      The plan is [$X] per month, billed automatically on the same day each month. Sales tax is added where it applies.

      ## Scheduling
      We'll contact you to book each visit. Visits that you cancel or miss without 24 hours' notice may not be rescheduled within the same month.

      ## Term and cancellation
      The plan renews month to month. Either of us may cancel at any time with [30] days' notice. Visits already paid for stay available until the end of the paid month.

      ## Our responsibility
      We are responsible for the quality of our work. We are not responsible for wear, misuse, weather, or damage caused by others, or for equipment we did not install or service.
    `),
  },
  {
    key: "plan-quarterly",
    name: "Maintenance plan: Quarterly",
    category: "Service Agreements",
    description: "Four visits a year (every three months), billed monthly or per visit.",
    requiresSignature: true,
    body: t(`
      This Maintenance Plan is between {{company.name}} ("we", "us") and {{customer.name}} ("you") for service at {{customer.address}}, starting {{date}}.

      ## What's included
      - Four scheduled maintenance visits per year, about every three months.
      - At each visit: a full inspection, adjustment, lubrication and safety test of the covered equipment, with a written summary of what we found.
      - Priority scheduling for repair calls.
      - [10%] off repair parts and labor while the plan is active.

      ## What's not included
      Parts, repairs and replacements are not included unless listed. We'll quote any repair and get your OK first.

      ## Price and billing
      [$X] per [month / visit], billed [monthly / after each visit]. Sales tax is added where it applies.

      ## Term and cancellation
      The plan runs for 12 months from the start date and renews for another 12 months unless either of us gives [30] days' written notice. If you cancel early, visits already performed are billed at our standard rate less what you've paid.

      ## Our responsibility
      We stand behind our workmanship. We are not responsible for normal wear, misuse, weather, or damage caused by others.
    `),
  },
  {
    key: "plan-annual",
    name: "Maintenance plan: Annual",
    category: "Service Agreements",
    description: "One or two tune-ups a year, paid yearly, renews each year.",
    requiresSignature: true,
    body: t(`
      This Annual Maintenance Plan is between {{company.name}} ("we", "us") and {{customer.name}} ("you") for service at {{customer.address}}, starting {{date}}.

      ## What's included
      - [One / Two] scheduled tune-up visits per plan year.
      - Each visit: a complete inspection, adjustment, lubrication and safety test, and a written report.
      - Priority scheduling and no trip charge on repair calls.
      - [10%] off repair parts and labor during the plan year.

      ## What's not included
      Repairs, parts and replacements are extra unless listed, and we'll always get your OK first.

      ## Price
      [$X] per year, due at the start of each plan year. Sales tax is added where it applies.

      ## Renewal and cancellation
      The plan renews automatically each year at the then-current price unless you tell us otherwise [30] days before the renewal date. We'll remind you before it renews. If you cancel within 30 days of paying and no visit has been performed, we'll refund the full price.

      ## Our responsibility
      We stand behind our workmanship. We are not responsible for normal wear, misuse, weather, or damage caused by others.
    `),
  },
  {
    key: "plan-weekly-commercial",
    name: "Service agreement: Weekly (commercial)",
    category: "Service Agreements",
    description: "Recurring weekly service for a business site, with response times and Net 30 billing.",
    requiresSignature: true,
    body: t(`
      This Service Agreement is between {{company.name}} ("Contractor") and {{customer.company}} {{customer.name}} ("Client") for the site at {{customer.address}}, effective {{date}}.

      ## Scope of service
      - A scheduled service visit each week on [day], during [hours].
      - Each visit: inspect, test and service the covered equipment listed in Schedule A, and record findings in a visit report.
      - Emergency response within [4] hours, [24/7 / during business hours], at the rates in Schedule B.

      ## Covered equipment (Schedule A)
      [List each door, gate, unit or system, with location and ID.]

      ## Rates (Schedule B)
      - Weekly service: [$X] per month.
      - Repairs outside scheduled visits: [$X] per hour plus parts.
      - Emergency call-out: [$X] plus hourly rate.

      ## Not-to-exceed
      Contractor will not perform repairs over [$X] per visit without Client's written approval.

      ## Billing
      Invoiced monthly in advance. Payment is due Net [30].

      ## Term and termination
      The initial term is [12] months and renews month to month after that. Either party may terminate with [30] days' written notice.

      ## Insurance
      Contractor maintains general liability and workers' compensation insurance and will provide certificates on request.
    `),
  },

  // ---------- Authorizations ----------
  {
    key: "work-authorization",
    name: "Work authorization (OK to start)",
    category: "Authorizations",
    description: "The customer's go-ahead on site before work begins, for repairs priced at the door.",
    requiresSignature: true,
    body: t(`
      I authorize {{company.name}} to perform the work described below at {{job.address}}.

      ## Work to be done
      {{job.title}}
      [Describe the repair, parts and any options the customer chose.]

      ## Price
      The price for this work is [$X] plus applicable tax, as reviewed with me before work began. If the technician finds something else that needs attention, they'll stop and get my OK before doing more.

      ## I understand that
      - Existing conditions that were not caused by this work (worn parts, rust, prior damage) are not covered.
      - Payment is due when the work is complete unless other terms were agreed in writing.
      - Parts and labor carry the warranty shown on my invoice.
    `),
  },
  {
    key: "change-order",
    name: "Change order",
    category: "Authorizations",
    description: "Approve added or changed work, its price and schedule impact, before it's done.",
    requiresSignature: true,
    body: t(`
      This Change Order changes the work for {{job.title}} at {{job.address}} (PO {{job.po}}), requested on {{date}}.

      ## Change requested
      [Describe what's being added, removed or changed, and why.]

      ## Price change
      - Original amount: [$X]
      - This change: [+/- $X]
      - New total: [$X]

      ## Schedule change
      [None / Adds X days / New completion date.]

      ## Terms
      All other terms of the original estimate or contract stay the same. Work on this change starts after this change order is signed.
    `),
  },

  // ---------- Completion ----------
  {
    key: "completion-signoff",
    name: "Job completion and sign-off",
    category: "Completion",
    description: "The customer confirms the work is done and walked through, with a space for notes.",
    requiresSignature: true,
    body: t(`
      I confirm that {{company.name}} completed the work for {{job.title}} at {{job.address}} on {{date}}.

      ## Walk-through
      - The technician showed me the finished work and how to operate it.
      - The work area was cleaned and old parts were [removed / left at my request].
      - I received information about my warranty.

      ## Notes
      [Anything still open, punch items, or comments.]

      If I notice a problem with this work, I'll contact {{company.name}} at {{company.phone}} and they'll come back to make it right under the warranty.
    `),
  },

  // ---------- Waivers and acknowledgments ----------
  {
    key: "existing-conditions",
    name: "Existing conditions acknowledgment",
    category: "Waivers",
    description: "Record damage or problems found before work starts, so nobody's surprised later.",
    requiresSignature: true,
    body: t(`
      Before starting work at {{job.address}}, the technician from {{company.name}} pointed out the following existing conditions:

      ## Conditions found
      [Describe each one: e.g., dented panel, rust at the bottom section, cracked header, worn opener gears, damaged weather seal.]

      ## I understand that
      - These conditions existed before {{company.name}} began work and were not caused by it.
      - The work quoted does not repair these conditions unless listed on my estimate.
      - Some conditions may affect how well the new work performs or how long it lasts.

      Photos of these conditions were taken on {{date}} and are kept with my job record.
    `),
  },
  {
    key: "customer-supplied-parts",
    name: "Customer-supplied parts waiver",
    category: "Waivers",
    description: "When the customer provides their own part or equipment for you to install.",
    requiresSignature: true,
    body: t(`
      I asked {{company.name}} to install a part or product that I purchased myself:

      [Part or product, brand and model.]

      ## I understand that
      - {{company.name}} did not supply this item and makes no warranty on it. Any product warranty is between me and the seller or manufacturer.
      - Labor is warranted for [90 days] for installation workmanship only.
      - If the item is defective, incorrect or incomplete, a return trip will be billed at the standard rate.
      - {{company.name}} is not responsible for damage caused by a failure of the item itself.
    `),
  },

  // ---------- Warranties ----------
  {
    key: "workmanship-warranty",
    name: "Workmanship warranty certificate",
    category: "Warranties",
    description: "A clean certificate to hand the customer: what's covered, for how long, how to claim.",
    requiresSignature: false,
    body: t(`
      {{company.name}} warrants the workmanship of the installation and repairs performed for {{customer.name}} at {{job.address}}, completed {{date}}.

      ## Coverage
      - Workmanship (installation and adjustment) is warranted for [1 year] from the completion date.
      - Parts and products are covered by their manufacturer's warranty. We'll help you file a claim.

      ## Not covered
      - Damage from impact, vehicles, weather, flooding, misuse or lack of maintenance.
      - Work or adjustments by anyone other than {{company.name}}.
      - Normal wear of springs, cables, rollers and weather seals beyond the manufacturer's rating.

      ## To make a claim
      Call {{company.phone}} or email {{company.email}} with your name and address. We'll schedule a visit, and covered work is done at no charge.
    `),
  },

  // ---------- Inspections ----------
  {
    key: "inspection-report",
    name: "Safety inspection report",
    category: "Inspections",
    description: "A pass / needs-attention checklist the tech fills out and the customer signs.",
    requiresSignature: true,
    body: t(`
      Inspection performed by {{company.name}} at {{job.address}} on {{date}}.

      ## Checklist (Pass / Needs attention)
      - Balance and spring condition: [Pass / Needs attention]
      - Cables and drums: [Pass / Needs attention]
      - Rollers, hinges and track: [Pass / Needs attention]
      - Auto-reverse safety test: [Pass / Needs attention]
      - Photo-eye sensors: [Pass / Needs attention]
      - Opener, rail and limits: [Pass / Needs attention]
      - Weather seal and bottom fixture: [Pass / Needs attention]

      ## Findings and recommendations
      [What needs attention, how urgent it is, and the recommended fix.]

      I received a copy of this report. Recommended repairs are not done until I approve an estimate.
    `),
  },
];

// Nevada lien documents: the statute's exact wording (liens.ts). Usually made
// from an invoice (waivers) or a project's lot (notice), which fill the blanks.
STARTER_TEMPLATES.push(
  ...LIEN_WAIVER_KINDS.map((kind) => ({
    key: `nv-${kind.replace("_", "-")}`,
    name: `${LIEN_DOC_TITLE[kind]} (Nevada)`,
    category: "Liens (Nevada)",
    description: `${LIEN_WAIVER_WHEN[kind]} Easiest from the invoice: Invoices → Lien waiver.`,
    requiresSignature: true,
    signer: "company" as const,
    body: lienWaiverBody(kind, {
      propertyName: "[Property name]",
      propertyLocation: "{{job.address}}",
      customer: "{{customer.name}}",
      invoiceNumber: "[Invoice or pay application number]",
      paymentAmount: "[$ amount]",
      paymentPeriod: "[Payment period]",
      disputedClaims: "[$ disputed, or $0.00]",
      dated: "{{date}}",
      companyName: "{{company.name}}",
    }),
  })),
  {
    key: "nv-notice-of-right-to-lien",
    name: "Notice of Right to Lien (Nevada)",
    category: "Liens (Nevada)",
    description: "For subs not hired by the owner: send within 31 days of first work. Easiest from the project: Lien notices.",
    requiresSignature: true,
    signer: "company" as const,
    body: lienNoticeBody({
      ownerNameAddress: "[Owner's name and address]",
      workDescription: "[General description of materials, equipment, work or services]",
      property: "[property description or street address]",
      contractWith: "{{customer.name}}",
      companyName: "{{company.name}}",
    }),
  },
);

/** Starter categories in the order the library shows them. */
export const STARTER_CATEGORIES = [...new Set(STARTER_TEMPLATES.map((s) => s.category))];

/**
 * A document body as plain terms (for estimate / invoice / plan terms that
 * print as-is): headings lose their ##, bullets become •, and lines that
 * only make sense filled per customer ({{...}}) are left out.
 */
export function plainTerms(body: string): string {
  return body
    .split(/\r?\n/)
    .filter((l) => !l.includes("{{"))
    .map((l) => l.replace(/^##\s+/, "").replace(/^-\s+/, "• "))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
